<template>
  <div class="sign-in-component-container">
    <div>
      <div class="heading">
        <img
          v-if="certApp"
          class="ase-logo"
          src="../../assets/images/ase/logo1.svg"
          alt=""
          role="presentation"
        >
        <img
          v-else
          :src="$store.getters['app/appLogo']"
          alt=""
          role="presentation"
        >
        <br>Welcome back!
      </div>
      <facebook />
      <div>
        <label
          class="title"
          for="email"
        >
          Email
        </label>
        <label
          v-if="emailInvalid"
          class="error"
        >
          Invalid Email
        </label>
      </div>
      <input
        id="email"
        v-model="email"
        :class="{ loading : loading }"
        type="text"
        @keyup.enter="handleSubmit"
      >
      <div>
        <label
          class="title"
          for="password"
        >
          Password
        </label>
        <label
          v-if="passwordInvalid"
          class="error"
        >
          Invalid Password
        </label>
      </div>
      <input
        id="password"
        v-model="password"
        :class="{ loading : loading }"
        type="password"
        @keyup.enter="handleSubmit"
      >
      <div class="bottom-container">
        <div class="error-container">
          <div
            v-if="message"
            class="error"
          >
            {{ message }}
          </div>
        </div>
        <button
          :class="{ loading : loading }"
          class="button"
          @click="handleSubmit"
        >
          {{ buttonText }}
        </button>
        <router-link
          :to="{ name: 'forgotPassword' }"
          class="suggestion"
        >
          Forgot Your Password?
        </router-link>
        <router-link
          v-if="!certApp"
          :to="{name: 'signUp'}"
          class="suggestion"
        >
          {{ createAccountText }}
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import Authentication from '@/mixins/authentication';
import Facebook from '@components/singletons/facebook';

export default {
  components: { Facebook },
  mixins: [Authentication],
  data() {
    return {
      buttonText: 'Log In',
    };
  },
  computed: {
    createAccountText() {
      return 'Don\'t have an account? Create Account';
    },
    emailInvalid() {
      return this.invalid.email;
    },
    passwordInvalid() {
      return this.invalid.password;
    },
  },
  created() {
    if (this.authenticated) {
      this.$router.push({name: 'home'});
    } else if (this.$route.query.email) {
      this.email = this.$route.query.email;
    }
  },
  methods: {
    handleSubmit() {
      const valid = this.validateInput();

      if (!valid) return;

      this.loading = true;
      this.buttonText = 'Logging In...';
      this.$store.dispatch('session/signIn', [{email: this.email, password: this.password}], null).then(response => {
        const redirectAfterLogin = this.$cookies.get('redirectAfterLogin');

        if (redirectAfterLogin) {
          this.$router.push(decodeURI(redirectAfterLogin)).then(() => {
            this.$cookies.remove('redirectAfterLogin');
          });
        } else {
          this.$router.push({ name: 'home' });
        }

        this.$notify({
          group: 'notifications',
          text: 'You are now logged in',
          type: 'success',
        });
      }).catch(e => {
        this.message = 'Incorrect Email/Password';
        setTimeout(() => { this.message = ''; }, 8000);
        this.buttonText = 'Log In';
        this.loading = false;
      });
    },
    validateInput() {
      let valid = true;
      if (!this.email || !/@.+\.\w+$/.test(this.email)) {
        valid = false;
        this.addInvalid('email');
      } else {
        this.removeInvalid('email');
      }

      if (!this.password || this.password.length < 8) {
        valid = false;
        this.addInvalid('password');
      } else {
        this.removeInvalid('password');
      }

      return valid;
    },
  },
};
</script>

<style lang='scss' scoped>
  $sign-in-modal-background: $white-1 !default;
  $sign-in-modal-suggestion: $grey-2 !default;
  $sign-in-modal-input-label: $grey-3 !default;
  $sign-in-modal-error-text: $red-1 !default;
  $sign-in-modal-button-background: $green-1 !default;
  $sign-in-modal-button-text: $white-1 !default;
  $sign-in-modal-title-text: $grey-2 !default;
  $sign-in-modal-input-border: $grey-4 !default;
  $sign-in-modal-input-error-border: $red-1 !default;
  $sign-in-modal-input-disabled: $grey-4 !default;

  .sign-in-component-container {
    height: 100%;

    @include breakpoint(small) {
      padding: rem-calc(32) !important;
    }

    > div {
      margin: auto;
      max-width: rem-calc(400);
    }

    .suggestion {
      color: $sign-in-modal-suggestion;
      cursor: pointer;
      display: block;
      @include font-size(12);
      text-align: center;
      text-decoration: underline;
      margin-bottom: rem-calc(12);
    }

    label{
      &.title {
        color: $sign-in-modal-input-label;
        display: inline-block;
        font-weight: 600;
      }

      &.error {
        color: red;
        float: right;
        font-size: 12px;
      }
    }

    .error-container .error {
      color: $sign-in-modal-error-text;
      @include font-size(12);
      margin-bottom: rem-calc(4);
      text-align: center;
    }

    input {
      border: 1px solid $sign-in-modal-input-border;
      border-radius: 4px;
      box-sizing: border-box;
      margin: rem-calc(4) 0 rem-calc(12) 0;
      width: 100%;

      &[type='password'] {
        letter-spacing: 1px;
      }

      &.error {
        border-color: $sign-in-modal-input-error-border;
      }

      &.loading {
        background-color: $sign-in-modal-input-disabled;
        pointer-events: none;
      }
    }

    .heading {
      color: $sign-in-modal-title-text;
      @include font-size(28);
      font-weight: $semi-bold;
      margin-bottom: rem-calc(24);
      text-align: center;

      img {
        max-width: rem-calc(48);

        &.ase-logo {
          margin-bottom: rem-calc(16);
          max-width: rem-calc(96);
          width: 100%;
        }
      }
    }

    .button {
      display: block;
      border: none;
      width: 100%;
      background-color: $sign-in-modal-button-background;
      border-radius: 4px;
      color: $sign-in-modal-button-text;
      margin-bottom: rem-calc(12);
      padding: rem-calc(10);
      text-align: center;

      &:active {
        opacity: 0.8;
      }

      &.loading {
        pointer-events: none;
      }
    }

    .bottom-container {
      margin-top: auto;
      text-align: center;
    }
  }
</style>
